import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Scale } from "lucide-react";
import { toast } from "sonner";
import { useUrlState } from "@/lib/navState";

const CARDS = [["invoice", "Total nilai invoice"], ["received", "Total uang diterima"], ["SURVEY", "Potongan biaya survei"], ["ADMIN", "Potongan administrasi"],
  ["TAX", "Pajak dipotong"], ["OTHER", "Potongan lainnya"], ["outstanding", "Piutang outstanding"], ["pending_review", "Selisih dalam pemeriksaan"]];
const ST = { PAID: "bg-emerald-600 text-white", SETTLED_WITH_DEDUCTIONS: "bg-emerald-100 text-emerald-800", PARTIALLY_PAID: "bg-blue-100 text-blue-800",
  UNDERPAID_PENDING_REVIEW: "bg-amber-100 text-amber-800", DISPUTED: "bg-red-100 text-red-700", UNPAID: "bg-gray-100 text-gray-600" };

function Deductions({ onDone }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/finance/deductions").then((r) => setRows(r.data || [])).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (d, approve) => {
    try {
      if (approve) {
        const code = d.account_code || window.prompt(`Akun COA untuk potongan ${d.type} (perlu klasifikasi Finance Manager):`) || "";
        if (!code) return;
        await api.post(`/finance/deductions/${d.id}/approve`, { account_code: code });
      } else {
        const reason = window.prompt("Alasan penolakan (nilai tetap menjadi piutang):") || "";
        if (!reason) return;
        await api.post(`/finance/deductions/${d.id}/reject`, { reason });
      }
      toast.success(approve ? "Disetujui & jurnal diposting" : "Ditolak"); load(); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!rows.length) return <p data-testid="ded-empty" className="mt-2 text-xs text-gray-400">Tidak ada potongan menunggu validasi.</p>;
  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-gray-100 bg-white">
      <table className="w-full min-w-[760px] text-left text-xs">
        <thead className="bg-gray-50 text-gray-500"><tr>{["Nopol", "SPK", "Invoice", "Jenis", "Nominal", "Akun", "Diinput", ""].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
        <tbody>{rows.map((d) => (
          <tr key={d.id} data-testid={`ded-row-${d.id}`} className="border-t border-gray-100">
            <td className="px-2 py-1.5">{d.license_plate}</td><td className="px-2 py-1.5">{d.spk_number}</td><td className="px-2 py-1.5">{d.invoice_number}</td>
            <td className="px-2 py-1.5 font-semibold">{d.type}</td><td className="px-2 py-1.5">{formatRupiah(d.amount)}</td>
            <td className="px-2 py-1.5">{d.account_code || <span className="font-semibold text-amber-700">Perlu klasifikasi Finance Manager</span>}</td>
            <td className="px-2 py-1.5">{d.created_by} · {String(d.created_at).slice(0, 16).replace("T", " ")}</td>
            <td className="px-2 py-1.5">{d.can_approve ? <div className="flex gap-1"><Button data-testid={`ded-approve-${d.id}`} size="sm" className="h-7 bg-emerald-600 text-white" onClick={() => act(d, true)}>Setujui & Posting</Button><Button data-testid={`ded-reject-${d.id}`} size="sm" variant="outline" className="h-7 text-red-600" onClick={() => act(d, false)}>Tolak</Button></div> : <span className="text-gray-400">Menunggu Finance Manager</span>}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export default function ReconDashboard() {
  const [insurer, setInsurer] = useUrlState("insurer", "");
  const [month, setMonth] = useUrlState("month", "");
  const [spk, setSpk] = useUrlState("spk", "");
  const [plate, setPlate] = useUrlState("plate", "");
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get("/finance/recon-dashboard", { params: { insurer, month, spk, plate } }).then((r) => setD(r.data)).catch(() => setD({ totals: {}, rows: [] })); }, [insurer, month, spk, plate]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div data-testid="recon-dashboard">
      <h1 className="font-heading text-2xl font-bold text-[#111827]"><Scale size={22} className="mr-2 inline text-blue-600" />Rekonsiliasi Pembayaran Asuransi</h1>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Input data-testid="recon-f-insurer" placeholder="Asuransi" value={insurer} onChange={(e) => setInsurer(e.target.value)} />
        <Input data-testid="recon-f-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <Input data-testid="recon-f-spk" placeholder="No. SPK" value={spk} onChange={(e) => setSpk(e.target.value)} />
        <Input data-testid="recon-f-plate" placeholder="Nopol" value={plate} onChange={(e) => setPlate(e.target.value)} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {CARDS.map(([k, l]) => <div key={k} data-testid={`recon-card-${k}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm"><div className="text-[11px] text-gray-500">{l}</div><div className="text-base font-bold text-gray-900">{formatRupiah(d?.totals?.[k] || 0)}</div></div>)}
      </div>
      <h2 className="mt-5 text-sm font-bold text-gray-700">Potongan menunggu validasi Finance Manager</h2>
      <Deductions onDone={load} />
      <h2 className="mt-5 text-sm font-bold text-gray-700">Rincian per invoice</h2>
      <div className="mt-2 overflow-x-auto rounded-lg border border-gray-100 bg-white">
        <table className="w-full min-w-[900px] text-left text-xs">
          <thead className="bg-gray-50 text-gray-500"><tr>{["Nopol", "SPK", "Invoice", "Tgl", "Asuransi", "Tagihan", "Diterima", "Potongan sah", "Pemeriksaan", "Outstanding", "Status"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
          <tbody>{(d?.rows || []).map((r) => (
            <tr key={r.claim_id} className="border-t border-gray-100">
              <td className="px-2 py-1.5">{r.license_plate}</td><td className="px-2 py-1.5">{r.spk_number}</td><td className="px-2 py-1.5">{r.invoice_number}</td><td className="px-2 py-1.5">{r.invoice_date}</td>
              <td className="px-2 py-1.5">{r.insurance_company}</td><td className="px-2 py-1.5">{formatRupiah(r.invoice_amount)}</td><td className="px-2 py-1.5">{formatRupiah(r.paid_amount)}</td>
              <td className="px-2 py-1.5">{formatRupiah(r.deductions_approved)}</td><td className="px-2 py-1.5">{formatRupiah(r.deductions_pending)}</td><td className="px-2 py-1.5 font-semibold">{formatRupiah(r.outstanding)}</td>
              <td className="px-2 py-1.5"><span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold ${ST[r.recon_status] || ""}`}>{r.recon_status.replace(/_/g, " ")}</span></td>
            </tr>))}</tbody>
        </table>
        {d && !d.rows.length && <p className="p-3 text-xs text-gray-400">Belum ada invoice untuk filter ini.</p>}
      </div>
    </div>
  );
}
